import { useState, useEffect, useRef } from "react";

export default function useActiveController(
  active = false,
  closingAnimationTime = 200,
  openingAnimation = true
): {
  view: boolean;
  animate: boolean;
} {
  const [view, setView] = useState<boolean>(!openingAnimation && active);
  const [animate, setAnimate] = useState<boolean>(!openingAnimation && active);

  const timeoutRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  // Handle activation and deactivation
  useEffect(() => {
    if (active) {
      if (!view) {
        // Show the component
        setView(true);
      }
    } else {
      if (animate) {
        // Stop the animation
        setAnimate(false);
      }

      if (view) {
        // Wait for the closing animation to finish before removing the component
        timeoutRef.current = window.setTimeout(() => {
          setView(false);
        }, closingAnimationTime);
      }
    }

    return () => {
      // Cleanup timeouts when the effect is re-run or the component unmounts
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [active, closingAnimationTime, view, animate]);

  // Start the animation after the component is rendered
  useEffect(() => {
    if (view && active && !animate) {
      // Use a double requestAnimationFrame to ensure the DOM has updated
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => {
          setAnimate(true);
        });
      });
    }

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [view, active, animate]);

  return {
    view,
    animate,
  };
}
